import {
  ChangeEvent,
  FC,
  MouseEvent as MouseEventReact,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { colors } from 'crono-fe-common/theme';
import { CcButton, EditableTemplateWrapper } from './style';
import { Template, TemplateVariables } from 'crono-fe-common/types/template';
import EditableTemplateModal from './editableTemplateModal';
import {
  Autocomplete,
  Button as MUIButton,
  ClickAwayListener,
  Grid,
  Typography,
} from '@mui/material';
import { useConfirmModal } from '../../../confirmModal';
import { useConditionalSnackBar, useSnackbar } from '../../../snackbar';
import { checkIfValidEmail } from 'utils/validationUtils';
import { formatHtmlContent } from 'utils/formatUtils';
import { calculateTemplateLength } from 'utils/businessLogic';
import {} from 'utils/businessLogic';
import { useLinkedinGetInvitationsLimit } from 'crono-fe-common/hooks/crono-extension/gateway';
import { Constants } from 'crono-fe-common/constants/constants';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { useAuth } from 'context/auth';
import useGetProspect from 'hooks/services/prospect/useGetProspect';
import useGetAccount from 'hooks/services/account/useGetAccount';
import CronoEditor from 'components/CronoEditor/CronoEditor';
import ReactQuill from 'react-quill';
import { useAttachmentsFilePicker } from 'hooks/useAttachmentsFilePicker';
import { useInsertTextAtCursor } from 'hooks/useInsertTextAtCursor';
import { FileContent } from 'use-file-picker';
import useGetTemplateAttachments from 'hooks/services/templates/useGetTemplateAttachments';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { ReactComponent as DocumentIcon } from 'crono-fe-common/icons/Document-Stroke.svg';
import { CronoAttachment } from 'crono-fe-common/types/cronoAttachment';
import { useSafeStrategyOverviewContext } from '../../index';
import { CustomButton } from 'components/CronoEditor/CronoEditorToolbar';
import LoadingOverlay from 'components/LoadingOverlay';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import useGetTemplateVariableExternalProperties from 'hooks/services/externalProperty/useGetTemplateVariableExternalProperties';
import { CustomTooltipVariables } from 'pages/templates/TemplateManagement';
import VariablesSelector from 'pages/templates/components/variablesSelector';
import { ReactComponent as VariablesIcon } from 'crono-fe-common/icons/Variables.svg';
import { replaceAllTemplateVariables } from 'crono-fe-common/utils';
import EmailInput from 'crono-fe-common/components/FormComponents/EmailInput';
import EmailAutoComplete from 'pages/accountDrawer/emailView/EmailAutoComplete';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ExpandableBox,
} from '@crono-one/design-system';
import { useSaveStepOnlyTemplate } from './useSaveStepOnlyTemplate';
import { PersonalizationIdentity } from 'hooks/strategy/usePersonalizationIdentity';

interface IProps {
  createNewTemplate?: boolean;
  previewOnly?: boolean;
  removeEmptyTemplate?: () => void;
  subject?: string;
  emailThreadButton?: ReactNode;
  enableSubjectMutation?: boolean;
  template: Template;
  stepId: number;
  conditionalStepId?: number;
  isABTesting?: boolean;
  isConditional?: boolean;
  onSuccess?: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;
  id?: string;
  //Function used to get the status of the edit also outside of the component (maybe to show something in parent component)
  setObserverEditMode?: (value: boolean) => void;

  onSaveReturnTemplate?: (
    subject: string,
    content: string,
    attachments?: (FileContent | CronoAttachment)[],
  ) => void;

  //For D variables substitution
  isSequenceInstance?: boolean;
  prospectId?: string | null;
  accountId?: string | null;
  //Who the variables resolve as; defaults to the logged-in user
  personalization?: PersonalizationIdentity;

  //For conditionals and ABTesting we want to be sure that, even in the case in which the same D is used for the same type (something that should not be possible) the editor does not crash
  uniqueIdentifierOfTemplate?: number;

  allowToPersonalize?: boolean;
  //HACK to make conditional styling on editMode=true
  applyIdOnlyOnEditMode?: boolean;

  isRewriting?: boolean;
  onCcChange?: (emails: string[]) => void;
  ccAddresses?: string[] | null;

  strategyId?: number;

  skipConfirm?: boolean;
  hideToolbar?: boolean;
  hideEditingButtons?: boolean;
  ignoreEmptyError?: boolean;
  variablesPickerOpenUpVariant?: boolean;
  onEditorFocus?: () => void;
  onEditorContentChange?: () => void;
}

interface SaveTemplateButtonProps {
  disabled: boolean;
  loading: boolean;
  showOptions: boolean;
  createNewTemplate: boolean;
  isMenuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onSave: () => void;
  onSaveForThisStepOnly: () => void;
}

const SaveTemplateButton = ({
  disabled,
  loading,
  showOptions,
  createNewTemplate,
  isMenuOpen,
  onMenuOpenChange,
  onSave,
  onSaveForThisStepOnly,
}: SaveTemplateButtonProps) => {
  if (!showOptions) {
    return (
      <Button onClick={onSave} disabled={disabled} size="sm" tone="primary">
        Save
      </Button>
    );
  }

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger
        render={
          <Button
            disabled={disabled}
            loading={loading}
            size="sm"
            tone="primary"
          >
            Save
          </Button>
        }
      />

      <DropdownMenuContent
        side="top"
        align="end"
        className="min-w-55"
        positionerClassname="z-50000"
      >
        <DropdownMenuItem onClick={onSave}>
          {createNewTemplate ? 'Create new template' : 'Edit template'}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={onSaveForThisStepOnly}>
          Save for this step only
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const EditableTemplate: FC<IProps> = ({
  createNewTemplate = false,
  previewOnly = false,
  removeEmptyTemplate,
  template,
  stepId,
  conditionalStepId,
  subject,
  emailThreadButton,
  enableSubjectMutation = true,
  isABTesting = false,
  isConditional = false,
  setObserverEditMode,
  onSuccess,
  id,
  onSaveReturnTemplate,
  isSequenceInstance = false,
  prospectId,
  accountId,
  personalization,
  uniqueIdentifierOfTemplate = 0,
  allowToPersonalize,
  applyIdOnlyOnEditMode,
  isRewriting = false,
  onCcChange,
  ccAddresses,
  strategyId,
  skipConfirm,
  hideToolbar,
  hideEditingButtons,
  ignoreEmptyError,
  onEditorFocus,
  onEditorContentChange,
  ...editorProps
}: IProps) => {
  const { user } = useAuth();
  const {
    insightsContext: {
      insightsCardRef,
      insightsButtonRef,
      prospect,
      setShowInsightTab,
      setRewriteEnabledMode,
      editorInFocus,
      setEditorInFocus,
      trackTemplateSaveIfNeeded,
      setRewriteSubmittedContext,
      isRewriteSequenceInstanceInProgress,
      selectedSequenceNotOwned,
    },
  } = useSafeStrategyOverviewContext();

  const account = prospect?.account;
  const prospectIdToUse = prospectId ?? prospect?.objectId;
  const accountIdToUse = accountId ?? account?.objectId;

  const { data: prospectData } = useGetProspect(
    !isSequenceInstance ? undefined : prospectIdToUse,
    true,
    false,
    false,
  );

  const { data: accountData } = useGetAccount(
    !isSequenceInstance ? undefined : (accountIdToUse ?? undefined),
  );

  const subjectSupport =
    template.type === TemplateType.IN_MAIL ||
    template.type === TemplateType.EMAIL;

  const {
    inputRef: subjectInputRef,
    handleSelect: handleSubjectSelect,
    insertTextAtCursor,
    resetSelection: resetSubjectSelection,
  } = useInsertTextAtCursor();

  const { data: templateVariables } = useGetTemplateVariables(
    personalization?.onBehalfUserId,
  );
  const externalProperties = useGetTemplateVariableExternalProperties();
  const personalizationUser = personalization ? personalization.user : user;

  const [editMode, setEditMode] = useState<boolean>(false);

  const resetParsedSubject = () => {
    resetSubjectSelection();
    setParsedSubject(
      subject !== undefined && subjectSupport
        ? parseTemplateVariablesSubject(subject)
        : undefined,
    );
  };

  useEffect(() => {
    // Every dependency here resolves asynchronously, so re-parsing while the
    // user is editing would overwrite their draft with the stored template.
    // editMode is deliberately not a dependency: leaving edit mode must not
    // re-parse, or a just-saved edit would revert until the template refetches.
    // Cancelling already re-parses through resetChanges.
    if (editMode) return;
    setTemplateContentValue(parseTemplateVariablesContent(template.content));
    resetParsedSubject();
  }, [
    prospectData?.data.data,
    accountData?.data.data,
    template.content,
    externalProperties,
    //Owner-scoped variables land after the step first renders, so without these
    //the preview would keep whatever the previous selection resolved to
    templateVariables?.data?.data,
    personalizationUser,
  ]);

  const parseTemplateVariablesContent = (content: string) => {
    if (!isSequenceInstance) {
      return formatHtmlContent(content);
    }
    return replaceAllTemplateVariables(
      formatHtmlContent(content),
      prospectData?.data?.data ?? null,
      accountData?.data.data ?? null,
      personalizationUser ?? null,
      templateVariables?.data?.data ?? null,
      externalProperties,
      template.type === TemplateType.EMAIL,
    );
  };

  const parseTemplateVariablesSubject = (subject: string) => {
    if (!isSequenceInstance) {
      return subject;
    }
    return replaceAllTemplateVariables(
      subject,
      prospectData?.data?.data ?? null,
      accountData?.data.data ?? null,
      personalizationUser ?? null,
      templateVariables?.data?.data ?? null,
      externalProperties,
      template.type === TemplateType.EMAIL,
    );
  };

  const parseSingleTemplateVariable = (variable: string) => {
    return replaceAllTemplateVariables(
      variable,
      prospectData?.data?.data ?? null,
      accountData?.data.data ?? null,
      personalizationUser ?? null,
      templateVariables?.data?.data ?? null,
      externalProperties,
      template.type === TemplateType.EMAIL,
    );
  };

  const [templateContentValue, setTemplateContentValue] = useState<string>(
    parseTemplateVariablesContent(template.content),
  );
  const [templateContentError, setTemplateContentError] =
    useState<boolean>(false);

  const [parsedSubject, setParsedSubject] = useState<string | undefined>(
    subject !== undefined && subjectSupport
      ? parseTemplateVariablesSubject(subject)
      : undefined,
  );

  useEffect(() => {
    resetParsedSubject();
  }, [subject]);

  const [subjectError, setSubjectError] = useState<boolean>(false);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [showToolbar, setShowToolbar] = useState<boolean>(false);

  useEffect(() => {
    if (!expanded && editorRef.current) {
      setShowToolbar(false);
      editorRef.current.blur();
    }
  }, [expanded]);

  //Replicate the editMode state to the parent component
  useEffect(() => {
    if (setObserverEditMode) {
      setObserverEditMode(editMode);
    }
  }, [editMode]);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isSaveMenuOpen, setIsSaveMenuOpen] = useState<boolean>(false);
  const [typedCharacters, setTypedCharacters] = useState<number>(0);

  //Personalize and the single task modal hand the content back to the caller
  //instead of creating a template, which changes both what saving can mean and
  //what leaving the editor should offer
  const createsTemplate = !onSaveReturnTemplate;

  const { openModal: openConfirmModal } = useConfirmModal();

  const resetChanges = () => {
    setTemplateContentValue(parseTemplateVariablesContent(template.content));
    resetParsedSubject();
    if (existingAttachments?.data?.data) {
      setAttachments(existingAttachments.data.data);
    }
  };

  const handleSave = () => {
    const sanitizedHTML = templateContentValue
      .replace(/(<([^>]+)>)/gi, '')
      .trim();
    if (sanitizedHTML || ignoreEmptyError) {
      //If I passed a function it means that I want the value to be returned instead of handled inside
      trackTemplateSaveIfNeeded(templateContentValue);
      if (onSaveReturnTemplate) {
        onSaveReturnTemplate(
          parsedSubject ?? '',
          formatHtmlContent(templateContentValue),
          attachments,
        );
        setEditMode(false);
        handleClose();
        return;
      }
      setModalVisible(true);
    } else if (!ignoreEmptyError) {
      setTemplateContentError(true);
    }
  };

  const handleCancel = () => {
    if (removeEmptyTemplate) {
      removeEmptyTemplate();
    }
    resetChanges();
    setEditMode(false);
    if (editorRef.current) {
      setShowToolbar(false);
      editorRef.current.blur();
    }
    // we submitted the rewrite, but then we didn't save the template -> analytics event should not be sent
    setRewriteSubmittedContext(undefined);
  };

  const handleClose = () => {
    setModalVisible(false);
  };

  const clickAwayHandler = (e: MouseEvent | TouchEvent | MouseEventReact) => {
    // The "Save template as" dialog is portalled out of this subtree, so its
    // own clicks reach the listener as clicks away from the editor.
    if (modalVisible) return;

    const target = e.target as HTMLElement;
    // if the click is outside the editor and the insights card / insights button
    if (
      (!insightsButtonRef?.current ||
        !insightsButtonRef.current.contains(target)) &&
      (!insightsCardRef?.current || !insightsCardRef.current.contains(target))
    ) {
      if (createNewTemplate && !editMode && removeEmptyTemplate) {
        removeEmptyTemplate();
      } else if (editMode) {
        e.preventDefault();
        e.stopPropagation();
        const sanitizedHTML = templateContentValue
          .replace(/(<([^>]+)>)/gi, '')
          .trim();

        if (!sanitizedHTML && !ignoreEmptyError) {
          if (
            removeEmptyTemplate &&
            !(createNewTemplate && subjectSupport && parsedSubject)
          ) {
            removeEmptyTemplate();
          } else {
            setTemplateContentError(true);
          }
        } else {
          if (skipConfirm) {
            handleSave();
            return;
          }

          openConfirmModal({
            //'info' puts Discard on the left as the outlined button and the
            //black one on the right
            type: 'info',
            title: createsTemplate
              ? 'Would you like to continue working on the content of this step?'
              : 'Would you like to save the contents of this task?',
            cancelText: 'Discard',
            cancelFunction: handleCancel,
            confirmText: createsTemplate ? 'Continue' : 'Save',
            confirmFunction: createsTemplate ? restoreEditingFocus : handleSave,
            showCloseCrossTopRight: true,
            //Discard is now the cancel action, so a stray click on the
            //background must not throw the changes away
            disableClickOnBackground: true,
          });
        }
      }
    }
  };

  const handleSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!editMode) {
      setEditMode(true);
      setExpanded(true);
    }
    setParsedSubject(e.target.value);
    onEditorContentChange?.();
  };

  const handleSuccess = (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => {
    onSuccess?.(data);

    setEditMode(false);
    handleClose();
  };

  const handleTitleBlur = () => {
    if (!parsedSubject) {
      if (!ignoreEmptyError) {
        setSubjectError(true);
      }
      resetParsedSubject();
    }
  };

  useEffect(() => {
    if (parsedSubject) {
      setSubjectError(false);
    }
  }, [parsedSubject]);

  useEffect(() => {
    if (templateContentError) {
      setTimeout(() => {
        setTemplateContentError(false);
      }, 2000);
    }
  }, [templateContentError]);

  const [maxLinkedinInvitationChars, setMaxLinkedinInvitationChars] =
    useState<number>(Constants.linkedinInvitationMaxChars);

  const { call: getLinkedinInvitationLimits } =
    useLinkedinGetInvitationsLimit();

  useEffect(() => {
    getLinkedinInvitationLimits().then((linkedinInvitationLimits) => {
      setMaxLinkedinInvitationChars(
        linkedinInvitationLimits?.maxMessageLength ??
          Constants.linkedinInvitationMaxChars,
      );
    });
  }, []);

  const editorRef = useRef<ReactQuill | null>(null);

  //The confirmation modal takes the focus, so continuing to edit has to put it
  //back where the user actually left off
  const lastFocusedFieldRef = useRef<'subject' | 'content'>('content');

  const restoreEditingFocus = () => {
    if (lastFocusedFieldRef.current === 'subject') {
      subjectInputRef.current?.focus();
      return;
    }
    editorRef.current?.focus();
  };

  const [attachments, setAttachments] = useState<
    (FileContent | CronoAttachment)[] | undefined
  >(undefined);

  const [openFileSelector, { filesContent, errorMessage }] =
    useAttachmentsFilePicker(attachments ?? []);

  const [showVariablesPicker, setShowVariablesPicker] =
    useState<boolean>(false);

  const { data: existingAttachments } = useGetTemplateAttachments(template.id);

  useEffect(() => {
    if (existingAttachments?.data?.data) {
      setAttachments(existingAttachments.data.data);
    }
  }, [existingAttachments]);

  const allowAttachments = useMemo(() => {
    return (
      template.type !== TemplateType.INVITATION &&
      template.type !== TemplateType.SCRIPT &&
      !isSequenceInstance
    );
  }, [template, isSequenceInstance]);

  const handleAttachments = (files: FileContent[]) => {
    if (!allowAttachments) return;
    if (files && files.length > 0) {
      setEditMode(true);
      setAttachments((prev) => (prev || []).concat(files));
    }
  };

  useEffect(() => {
    handleAttachments(filesContent);
  }, [filesContent]);

  const removeAttachment = (index: number) => {
    setAttachments((attachments) => attachments?.filter((_, i) => i !== index));
  };

  const { saveStepOnlyTemplate, isSaveStepOnlyTemplateLoading } =
    useSaveStepOnlyTemplate({
      stepId,
      conditionalStepId,
      isABTesting,
      isConditional,
      strategyId,
      onSuccess: handleSuccess,
    });

  const handleSaveForThisStepOnly = async () => {
    const sanitizedHTML = templateContentValue
      .replace(/(<([^>]+)>)/gi, '')
      .trim();

    if (!sanitizedHTML && !ignoreEmptyError) {
      setTemplateContentError(true);
      return;
    }

    trackTemplateSaveIfNeeded(templateContentValue);

    try {
      await saveStepOnlyTemplate({
        template,
        subject: parsedSubject ?? '',
        content: templateContentValue,
        attachments: attachments ?? [],
        initialAttachments: existingAttachments?.data?.data ?? [],
      });
    } catch {
      //The failed mutation already reports itself through the global handler.
      //Swallowing here only keeps the editor open on the unsaved content
      //instead of leaving an unhandled rejection behind.
    }
  };

  useConditionalSnackBar([
    {
      condition: subjectError,
      message: "Can't save with empty Subject",
      severity: 'error',
    },
    {
      condition: templateContentError,
      message: "You can't save template without the text",
      severity: 'error',
    },
    {
      condition: !!errorMessage,
      message: errorMessage ?? 'Error uploading file',
      severity: 'error',
    },
  ]);

  const toolbarId = `toolbar-${template.id}-${stepId}-${uniqueIdentifierOfTemplate}`;

  // [HACK] To make toolbar visible when click on dropdown since we hide it on editor blur
  useEffect(() => {
    const toolbarElement = document.getElementById(toolbarId);

    const handleClick = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      if (!target.id.includes('ql-rewrite')) {
        setEditMode(true);
      }
    };

    if (toolbarElement) {
      toolbarElement.addEventListener('click', handleClick);

      return () => {
        toolbarElement.removeEventListener('click', handleClick);
      };
    }
  }, [toolbarId]);

  const onRewriteWithAIClick = () => {
    setShowInsightTab((prevShowInsightTab) => {
      let prevRewriteEnabledMode;
      setRewriteEnabledMode((prev) => {
        prevRewriteEnabledMode = prev;
        return prevShowInsightTab && prev ? null : 'template';
      });

      return !(prevShowInsightTab && prevRewriteEnabledMode);
    });
  };

  // we update reference of editorInFocus one that got template changed
  useEffect(() => {
    if (editorInFocus !== null) {
      setEditorInFocus(editorRef.current);
    }
  }, [template]);

  const customToolbarButtons = useMemo(() => {
    const customButtons: CustomButton[] = [];

    if (allowAttachments) {
      customButtons.push({
        type: 'attachments',
        onClick: openFileSelector,
      });
    }

    customButtons.push({
      type: 'variables',
      showLabel: true,
      onClick: () => setShowVariablesPicker((prev) => !prev),
    });

    //Matches the toolbar action in the personalize header: the rewrite runs in
    //this user's browser extension and would personalise as the wrong person
    if (isSequenceInstance && allowToPersonalize && !selectedSequenceNotOwned) {
      customButtons.push({
        type: 'rewrite',
        showLabel: true,
        onClick: onRewriteWithAIClick,
      });
    }

    return customButtons;
  }, [
    template.type,
    isSequenceInstance,
    allowAttachments,
    allowToPersonalize,
    selectedSequenceNotOwned,
  ]);

  const [variablesTooltipSubject, setVariablesTooltipSubject] =
    useState<boolean>(false);

  const addVariablesSubject = (variable: TemplateVariables | string) => {
    setParsedSubject(insertTextAtCursor(parsedSubject ?? '', `${variable}`));
    setVariablesTooltipSubject(false);
  };

  const [showCc, setShowCc] = useState(!!ccAddresses?.length);
  const { pushSnackbar } = useSnackbar();

  // EmailAutoComplete state for CC field
  const [ccQuery, setCcQuery] = useState<string>('');
  const [isCcFocused, setIsCcFocused] = useState<boolean>(false);

  // Clear CC query when focus is lost
  useEffect(() => {
    if (!isCcFocused) {
      setCcQuery('');
    }
  }, [isCcFocused]);

  const handleCcChange = (value: string[]) => {
    const trimmedValue = value.map((v) => v.trim());

    if (trimmedValue.some((v) => !v || !checkIfValidEmail(v))) {
      pushSnackbar({
        severity: 'error',
        message: 'Please enter a valid email address',
      });
      return;
    }

    onCcChange!(trimmedValue);
    if (trimmedValue.length === 0) {
      setShowCc(false);
    }
  };

  // Handle EmailAutoComplete selection for CC field
  const handleCcEmailSelect = (email: string) => {
    const currentCcEmails = ccAddresses || [];
    if (!currentCcEmails.includes(email)) {
      const updatedEmails = [...currentCcEmails, email];
      onCcChange!(updatedEmails);
    }
    setCcQuery('');
  };

  return (
    <EditableTemplateWrapper
      id={
        id && (applyIdOnlyOnEditMode ? editMode : true)
          ? id
          : 'editable-template-wrapper'
      }
    >
      <ClickAwayListener
        onClickAway={clickAwayHandler}
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
      >
        <div
          className="container-editable-template"
          id={`container-editable-${toolbarId}`}
        >
          {parsedSubject !== undefined && (
            <FlexDiv direction="column" alignItems="start" gap="0px">
              <div className="email-subject-container">
                <div className="email-subject">
                  <Typography
                    fontSize={14}
                    lineHeight={'16px'}
                    color={colors.grey11}
                  >
                    Subject:
                  </Typography>

                  {enableSubjectMutation || createNewTemplate ? (
                    <input
                      ref={subjectInputRef}
                      className="email-subject-input"
                      onChange={handleSubjectChange}
                      onSelect={handleSubjectSelect}
                      onFocus={() => {
                        lastFocusedFieldRef.current = 'subject';
                      }}
                      onBlur={handleTitleBlur}
                      value={parsedSubject}
                      disabled={previewOnly}
                    />
                  ) : (
                    <span>{parsedSubject}</span>
                  )}
                </div>

                {!showCc && onCcChange && (
                  <CcButton onClick={() => setShowCc(true)}>Cc:</CcButton>
                )}

                {subjectSupport && (
                  <ClickAwayListener
                    onClickAway={() => setVariablesTooltipSubject(false)}
                  >
                    <div>
                      <CustomTooltipVariables
                        open={variablesTooltipSubject}
                        onClose={() => setVariablesTooltipSubject(false)}
                        title={
                          <VariablesSelector
                            addVariables={addVariablesSubject}
                          />
                        }
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        sx={{ zIndex: '5001 !important' }}
                      >
                        <MUIButton
                          className="add-variables-button"
                          color="secondary"
                          startIcon={<VariablesIcon />}
                          onClick={() =>
                            setVariablesTooltipSubject((prev) => !prev)
                          }
                        >
                          <span id="text">Subject variables</span>
                        </MUIButton>
                      </CustomTooltipVariables>
                    </div>
                  </ClickAwayListener>
                )}

                {emailThreadButton && !previewOnly && !createNewTemplate && (
                  <div onClick={clickAwayHandler}>{emailThreadButton}</div>
                )}
              </div>

              {showCc && onCcChange && (
                <div className="email-cc">
                  <Typography
                    fontSize={14}
                    lineHeight={'16px'}
                    color={colors.grey11}
                  >
                    Cc:
                  </Typography>

                  <Grid item container>
                    <div className="relative w-full">
                      <Autocomplete
                        autoSelect
                        freeSolo
                        multiple
                        id="cc"
                        fullWidth
                        value={ccAddresses ?? []}
                        inputValue={ccQuery}
                        onInputChange={(_, newInputValue) => {
                          setCcQuery(newInputValue);
                        }}
                        onChange={(_, value) => handleCcChange(value)}
                        options={[]}
                        renderInput={(params) => (
                          <EmailInput
                            {...params}
                            className="form-field"
                            fullWidth
                            placeholder="Enter email"
                            onFocus={() => setIsCcFocused(true)}
                            onBlur={() => setIsCcFocused(false)}
                          />
                        )}
                      />
                      {isCcFocused && ccQuery.length > 0 && (
                        <EmailAutoComplete
                          query={ccQuery}
                          onSelect={handleCcEmailSelect}
                          emailsAlreadySelected={ccAddresses}
                        />
                      )}
                    </div>
                  </Grid>
                </div>
              )}
            </FlexDiv>
          )}
          <LoadingOverlay
            isLoading={isRewriteSequenceInstanceInProgress || isRewriting}
            loadingText={'Rewriting template...'}
            className={`full-width ${!hideToolbar && (showToolbar || (editMode && expanded)) ? '' : 'toolbar-hidden'}`}
          >
            <ExpandableBox
              expanded={expanded}
              onExpandedChange={setExpanded}
              hideToggle={editMode}
            >
              <CronoEditor
                readOnly={previewOnly}
                whiteBackground
                toolbarId={toolbarId}
                editorRef={editorRef}
                value={templateContentValue}
                imageSupport={template.type === TemplateType.EMAIL}
                placeholder={'Type text...'}
                hideStylingButtons={template.type !== TemplateType.EMAIL}
                showVariablesPicker={showVariablesPicker}
                setShowVariablesPicker={setShowVariablesPicker}
                customButtonsToRight={false}
                parseVariable={
                  isSequenceInstance ? parseSingleTemplateVariable : undefined
                }
                disableReplaceSnippetAfterCreation={isSequenceInstance}
                onAttachmentDrop={handleAttachments}
                onBlur={() => setShowToolbar(false)}
                onFocus={() => {
                  if (previewOnly) return;

                  lastFocusedFieldRef.current = 'content';
                  if (editorInFocus !== editorRef.current) {
                    setEditorInFocus(editorRef.current);
                  }
                  setExpanded(true);
                  setShowToolbar(true);
                  onEditorFocus?.();
                }}
                onChange={(text, _, source) => {
                  if (previewOnly) return;

                  if (!editMode && source === 'user') {
                    setEditMode(true);
                    setExpanded(true);
                  }
                  setTemplateContentValue(text);

                  setTypedCharacters(calculateTemplateLength(text, true) ?? 0);
                  onEditorContentChange?.();
                }}
                customButtons={hideToolbar ? undefined : customToolbarButtons}
                hideToolbar={hideToolbar}
                {...editorProps}
              >
                <>
                  {expanded && attachments && attachments.length > 0 ? (
                    <div className="file-preview">
                      {attachments?.map((attachment, index) => (
                        <div className="file" key={index}>
                          {isSequenceInstance ? (
                            <DocumentIcon />
                          ) : (
                            <CloseMIcon
                              className="delete-attachments-icon"
                              onClick={() => {
                                setEditMode(true);
                                removeAttachment(index);
                              }}
                            />
                          )}
                          {attachment.name}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <></>
                  )}
                  {editMode && !hideEditingButtons && (
                    <div
                      className="template-editing-buttons"
                      style={{ alignItems: 'center' }}
                    >
                      {template.type === TemplateType.INVITATION && (
                        <>
                          <Typography
                            color={colors.grey11}
                            fontSize={'12px'}
                            fontWeight={500}
                            lineHeight={'18px'}
                            minWidth={'fit-content'}
                          >
                            {typedCharacters} / {maxLinkedinInvitationChars}
                          </Typography>
                        </>
                      )}
                      <Button
                        onClick={handleCancel}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>

                      <SaveTemplateButton
                        disabled={
                          template.type === TemplateType.INVITATION &&
                          typedCharacters > maxLinkedinInvitationChars
                        }
                        loading={isSaveStepOnlyTemplateLoading}
                        showOptions={createsTemplate}
                        createNewTemplate={createNewTemplate}
                        isMenuOpen={isSaveMenuOpen}
                        onMenuOpenChange={setIsSaveMenuOpen}
                        onSave={handleSave}
                        onSaveForThisStepOnly={handleSaveForThisStepOnly}
                      />
                    </div>
                  )}
                </>
              </CronoEditor>
            </ExpandableBox>
            {editMode && (
              <FlexDiv
                style={{
                  alignItems: 'flex-start',
                }}
              >
                {template.type === TemplateType.INVITATION && (
                  <>
                    {/* <Typography
                      color={colors.grey11}
                      fontSize={'12px'}
                      fontWeight={500}
                      lineHeight={'18px'}
                      minWidth={'fit-content'}
                    >
                      {typedCharacters} / {maxLinkedinInvitationChars}
                    </Typography> */}
                    {typedCharacters > maxLinkedinInvitationChars ? (
                      <Typography
                        color={colors.inactive}
                        fontSize={'12px'}
                        fontWeight={500}
                        lineHeight={'18px'}
                        minWidth={'fit-content'}
                      >
                        The maximum number of characters for a Linkedin
                        invitation is {maxLinkedinInvitationChars}
                      </Typography>
                    ) : null}
                  </>
                )}
              </FlexDiv>
            )}
          </LoadingOverlay>

          {modalVisible && (
            <EditableTemplateModal
              createNewTemplate={createNewTemplate}
              close={handleClose}
              onSuccess={handleSuccess}
              template={template}
              stepId={stepId}
              conditionalStepId={conditionalStepId}
              content={templateContentValue}
              subject={parsedSubject ?? ''}
              attachments={attachments ? attachments : []}
              initialAttachments={
                existingAttachments?.data?.data
                  ? existingAttachments.data.data
                  : []
              }
              isABTesting={isABTesting}
              isConditional={isConditional}
              strategyId={strategyId}
            />
          )}
        </div>
      </ClickAwayListener>
    </EditableTemplateWrapper>
  );
};

export default EditableTemplate;
